import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { FeaturesFacade } from '@core/features/facades/features.facade';
import { Feature } from '@kaseya-shared';
import { NavigationFacade } from '@core/navigation/facades/navigation.facade';

/**
 * @param feature A route feature flag.
 * @param skipAccessDeniedPage If true, angular will keep finding another route to match. Useful when you want to implement feature flag on a route level.
 * ```
 * {
 *   path: 'kaseya-365-setup-guide',
 *   canMatch: [isFeatureEnabledGuard(Feature.Kaseya365SetupGuide, true)],
 *   loadComponent: NewKaseya365SetupGuideComponent
 * },
 * {
 *   path: 'kaseya-365-setup-guide',
 *   loadComponent: OldKaseya365SetupGuideComponent
 * }
 * ```
 */
export const isFeatureEnabledGuard =
  (feature: Feature, skipAccessDeniedPage = false): CanMatchFn =>
  ({ path }) => {
    const router = inject(Router);
    const featuresFacade = inject(FeaturesFacade);
    const navigationFacade = inject(NavigationFacade);

    featuresFacade.loadFeatures();
    const isFeatureEnabled = featuresFacade.isFeatureEnabled(feature);
    return featuresFacade.featuresLoaded$.pipe(
      map(() => {
        if (isFeatureEnabled()) {
          return true;
        }

        if (skipAccessDeniedPage) {
          return false;
        }

        if (navigationFacade.redirectLeftNavigation() === path && navigationFacade.nextRedirectLeftNavigation()) {
          return navigationFacade.redirectToNextLeftNavigation();
        }

        return router.createUrlTree(['access-denied']);
      })
    );
  };
